import React, { useState, useMemo, useEffect } from "react";

import {
  Typography,
  TextField,
  Tooltip,
  Checkbox,
  useTable,
  TableMetadata,
  Button,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as Download } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ReactComponent as Favorite } from "@king-ict/eupisi-ui/components/Icon/Favorite.svg";
import { CodeBook, EnrollmentTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router";

import {
  getInstitutionsProgramsLocations as getInstitutionsProgramsLocationsApi,
  getInstitutionFounderEnrollmentGroup as getInstitutionFounderEnrollmentGroupApi,
  searchEnrollmentList as searchEnrollmentListApi,
  cancelConfirmedEnrollmentRequests as cancelConfirmedEnrollmentRequestsApi,
  getPedagogicalYears as getPedagogicalYearsApi,
  getMonths as getMonthsApi,
  exportEnrollmentList as exportEnrollmentListApi
} from "../../../api";
import {
  confirmedEnrollmentListRoute,
  preschoolConfirmedEnrollmentListRoute,
  subsequentConfirmedEnrollmentListRoute
} from "../../../app";
import { EnrollmentListHeader, EnrollmentList } from "../../../components";
import { useBusinessRule } from "../../../hooks";
import {
  EnrollmentFilters,
  EnrollmentSearchItem,
  LocationProgramListItem,
  InstitutionListItem,
  Enrollment,
  EnrollmentAllowedActionsEnum,
  InstitutionFounderEnrollmentGroup,
  RequestTypeEnum,
  PedagogicalYear,
} from "../../../models";
import style from "../EnrollmentList.module.scss";
import { EnrollmentSearchFilters } from "../EnrollmentSearchFilters";

interface Props {
  requestTypeId: RequestTypeEnum;
  enrollmentTypeId: EnrollmentTypeEnum;
}

export const ConfirmedEnrollmentListPage = ({
  requestTypeId,
  enrollmentTypeId
}: Props) => {
  const { t } = useTranslation();
  const { canEvaluateEnrollment } = useBusinessRule();
  const createInitialEnrollmentFilter = (): EnrollmentFilters => {
    return {
      childOib: undefined,
      childLastName: undefined,
      kindergarten: undefined,
      location: undefined,
      program: undefined,
      enrollmentStatusId: 2,
      requestTypeId: requestTypeId,
      enrollmentTypeId: enrollmentTypeId,
      monthId: undefined,
      filterByConfirmedLocation: true
    } as EnrollmentFilters
  }

  const [filters, setFilters] = useState<EnrollmentFilters>(() => createInitialEnrollmentFilter());
  const [enrollmentLists, setEnrollmentLists] = useState<
    PagedResult<EnrollmentSearchItem>
  >(({
    rowCount: 0,
    results: [],
  } as unknown) as PagedResult<EnrollmentSearchItem>);

  const preschoolConfirmedEnrollmentListMatch = useRouteMatch(
    preschoolConfirmedEnrollmentListRoute
  );
  const subsequentConfirmedEnrollmentListMatch = useRouteMatch(
    subsequentConfirmedEnrollmentListRoute
  );

  const [institutionsCapacities, setInstitutionsCapacities] = useState<
    InstitutionListItem[]
  >();

  const [
    institutionFounderEnrollmentGroup,
    setinstitutionFounderEnrollmentGroup,
  ] = useState<InstitutionFounderEnrollmentGroup>();

  const [pedagogicalYears, setPedagogicalYears] = useState<
    PedagogicalYear[]
  >([]);

  const [months, setMonths] = useState<
    CodeBook[]
  >([]);

  const [pedagogicalYearId, setPedagogicalYearId] = useState<number>();
  const [monthId, setMonthId] = useState<number>();

  const [checkedEnrollmentRequests, setCheckedEnrollmentRequests] = useState<
    Enrollment[]
  >([]);

  const [
    openCancelEnrollmentConfirmModal,
    setOpenCancelEnrollmentConfirmModal,
  ] = useState(false);
  const [
    openEnrollmentCancelledModal,
    setOpenEnrollmentCancelledModal,
  ] = useState(false);

  const handleToggleCancelEnrollmentConfirmModal = () => {
    setOpenCancelEnrollmentConfirmModal(
      (prevOpenCancelEnrollmentConfirmModal) =>
        !prevOpenCancelEnrollmentConfirmModal
    );
  };

  const handleToggleEnrollmentCancelledModal = () => {
    setOpenEnrollmentCancelledModal(
      (prevEnrollmentCancelledModal) => !prevEnrollmentCancelledModal
    );
  };

  const handleCancelEnrollment = async () => {
    setOpenCancelEnrollmentConfirmModal(false);

    await cancelConfirmedEnrollmentRequestsApi(checkedEnrollmentRequests)
      .then(() => setCheckedEnrollmentRequests([]))
      .then(() => setOpenEnrollmentCancelledModal(true));
  };

  useEffect(() => {
    const loadInitialData = async () => {
      const initialLoadPromises = [] as Promise<void>[];

      const pedagogicalYearsPromise = 
        getPedagogicalYearsApi().then(result => { 
          setPedagogicalYears(result);
          
          const activePedagogicalYearId = result.find(py => py.isActive)?.id;
          if (activePedagogicalYearId) {
            setPedagogicalYearId(activePedagogicalYearId);
            return;
          }

          if (result.length > 0) {
            setPedagogicalYearId(result[result.length - 1].id);
            return;
          }
        });
      initialLoadPromises.push(pedagogicalYearsPromise);
      
      const institutionFounderEnrollmentGroupPromise =
        getInstitutionFounderEnrollmentGroupApi().then(result => setinstitutionFounderEnrollmentGroup(result));
      initialLoadPromises.push(institutionFounderEnrollmentGroupPromise);

      if (enrollmentTypeId === EnrollmentTypeEnum.SubsequentEnrollment) {
        const monthsPromise = 
          getMonthsApi().then(result => setMonths(result));
        initialLoadPromises.push(monthsPromise)
      }

      await Promise.all([initialLoadPromises]);
    }
    loadInitialData();
  // this is the initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pedagogicalYearId) {
      return;
    }
    const newFilter = createInitialEnrollmentFilter();
    setFilters(newFilter);
    setTableFilters(newFilter);

    updatetInstitutionsCapacities();
  }, [pedagogicalYearId]);

  useEffect(() => {
    if (!pedagogicalYearId) {
      return;
    }
    const newFilter = createInitialEnrollmentFilter();
    setFilters(newFilter);
    setTableFilters(newFilter);
  }, [monthId]);

  const initialTableMetadata = useMemo(
    () =>
      ({
        rowKey: "id",
        rowToolTip: () => {},
        columns: [],
      } as TableMetadata<EnrollmentSearchItem>),
    []
  );

  const [tableProps, tableState, setTableFilters] = useTable<EnrollmentFilters>(
    {
      metadata: initialTableMetadata,
      data: enrollmentLists.results,
      enrollmentTable: true,
      noHover: true,
      pagination: true,
      totalItems: enrollmentLists.rowCount,
    }
  );

  useEffect(() => {
    if (!pedagogicalYearId) {
      return;
    }
    search();
  }, [tableState]);

  const search = async () => {
    if (!pedagogicalYearId) {
      return;
    }
    const results = await searchEnrollmentListApi({
      ...tableState,
      ...filters,
      pedagogicalYearId: pedagogicalYearId,
      monthId: monthId
    });
    setEnrollmentLists(results);
    setCheckedEnrollmentRequests([]);
  };

  const handleSearch = () => {
    setTableFilters({
      ...filters,
    });
    setCheckedEnrollmentRequests([]);
  };

  const handleClear = () => {
    const initialFilter = createInitialEnrollmentFilter();
    initialFilter.pedagogicalYearId = filters.pedagogicalYearId;
    setFilters(initialFilter);
  };

  const handleDownloadExcel = () => {
    if (!pedagogicalYearId) {
      return;
    }

    exportEnrollmentListApi({
      ...filters,
      pedagogicalYearId: pedagogicalYearId,
      monthId: monthId
    });
  };

  const renderLocationsAndProgramsColumn = (
    enrollmentRequest: EnrollmentSearchItem
  ) => {
    return (
      <React.Fragment key={`frag_${enrollmentRequest.id}`}>
        <Row className={style.margin0}>
          <Col xs={12}>
            <Tooltip
              placement="bottom-start"
              overlay={
                <div className={style.institutionCriteriaTooltipInfo}>
                  <Row className={style.margin0}>
                    <Col xs={12} className={`${style.preferenceTitle}`}>
                      <Typography
                        variant="smallHeading"
                        component="h3"
                        color="light"
                      >
                        {t("enrollmentList.preferences")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={`${style.margin0}`}>
                    {renderPreferenceData(
                      enrollmentRequest.locationsAndPrograms,
                      enrollmentRequest.suggestedPedagogicalYearInstitutionProgramDetailId
                    )}
                  </Row>
                </div>
              }
            >
              <div className={style.infoSection}>
                <Favorite />
                <Typography variant="base" component="h3" color="primary">
                  {t("enrollmentList.allPreferences")}
                </Typography>
              </div>
            </Tooltip>
          </Col>
        </Row>
        <Row className={`${style.locations} ${style.margin0}`}>
          <Col xs={12}>
          <TextField
              name="parentInstitutionName"
              value={enrollmentRequest.parentInstitutionName}
              readonly
              plaintext              
              label={t("enrollmentList.institution")}
            />
            <TextField
              name="location"
              value={enrollmentRequest.locationName}
              readonly
              plaintext              
              helperText={enrollmentRequest.programName}
              label={t("enrollmentList.locationAndProgram")}
            />
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  const renderPreferenceData = (
    data: LocationProgramListItem[],
    suggestedPedagogicalYearInstitutionProgramDetailId?: number
  ) => {
    return data.map((data, i) => {
      return (
        <Col
          xs={12}
          className={`${style.preferenceTitle} ${
            data.id === suggestedPedagogicalYearInstitutionProgramDetailId
              ? style.preferencePriority
              : ""
          }`}
          key={`prefData_${data.id}`}
        >
          <TextField
            type="text"
            label={data.locationName}
            value={data.programName}
            plaintext
            boldLabel
            lightText
          />
        </Col>
      );
    });
  };

  const renderCheckBox = (enrollmentRequest: EnrollmentSearchItem) => {
    return (      
      canEvaluateEnrollment() && (
        <Checkbox
          checked={
            checkedEnrollmentRequests.find(
              (x) => x.id === enrollmentRequest.id
            ) != null
          }
          value={enrollmentRequest.id}
          onChange={(e) =>
            handleRowCheckBoxClick(
              e,
              enrollmentRequest.pedagogicalYearInstitutionProgramDetailId!,
              enrollmentRequest.locationId
            )
          }
        />
      )        
    );
  };

  const handleRowCheckBoxClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    programDetailsId: number,
    locationId?: number
  ) => {
    const { value, checked } = e.target;

    handleRowCheckBoxClickExtension(
      +value,
      checked,
      programDetailsId,
      locationId
    );    
  };

  const handleRowCheckBoxClickExtension = (
    value: number,
    checked: boolean,
    programDetailsId: number,
    locationId?: number
  ) => {
    if (checked) {
      setCheckedEnrollmentRequests((prev) => [
        ...prev,
        {
          id: +value,
          allowedActions: EnrollmentAllowedActionsEnum.All,
          institutionId: 0,
          locationId: locationId ?? 0,
          pedagogicalYearInstitutionProgramDetailId: programDetailsId ?? 0,
        },
      ]);
    } else {
      const checkedRequests = checkedEnrollmentRequests.filter(
        (x) => x.id !== +value
      );
      setCheckedEnrollmentRequests(checkedRequests);
    }
  };

  const handleBackToEnrollmentList = () => {
    handleToggleEnrollmentCancelledModal();
    updateTable();
  };

  const updatetInstitutionsCapacities = async () => {
    if (pedagogicalYearId) {
      await getInstitutionsProgramsLocationsApi(pedagogicalYearId).then(
        result => {
          setInstitutionsCapacities(result);
        }
      )
    };
  }

  const updateTable = () => {
    handleSearch();
    search();
    updatetInstitutionsCapacities();
  }

  const handleSelectlAll = () => { 
    setCheckedEnrollmentRequests([]);
    enrollmentLists.results.forEach(enrollment => {
      handleRowCheckBoxClickExtension(
        enrollment.id,
        true,        
        enrollment.pedagogicalYearInstitutionProgramDetailId!,
        enrollment.locationId
      )
    });
  };

  const handleCancelAll = () => {
    setCheckedEnrollmentRequests([]);
  };

  return (
    <>
      <EnrollmentListHeader
        pedagogicalYearId={pedagogicalYearId}
        pedagogicalYears={pedagogicalYears}
        onChangePedagogicalYear={(e) => { setPedagogicalYearId(e.target.value) }}
        monthId={monthId}
        months={months}
        onChangeMonth={(e) => { setMonthId(e.target.value); }}
        infoMessage={
          institutionFounderEnrollmentGroup?.institutionName
            ? t("enrollmentList.confirmedEnrollmentInfoMessage", {
                kindergartenName:
                  institutionFounderEnrollmentGroup?.institutionName,
                founderName: institutionFounderEnrollmentGroup?.founderName,
              })
            : t("enrollmentList.confirmedEnrollmentGroupInfoMessage", {
                enrollmentGroupName:
                  institutionFounderEnrollmentGroup?.enrollmentGroupName,
              })
        }
        title={t("enrollmentList.confirmedListTitle")}
        institutionsCapacities={institutionsCapacities!}
      />
      <EnrollmentSearchFilters
        handleSearch={handleSearch}
        handleClear={handleClear}
        filters={filters}
        setFilters={setFilters}
        institutionsLocationsAndPrograms={institutionsCapacities}
        enrollmentStatusId={2}
      />
      <Row>
        <Col md={12}>
        <Button className={style.checkAllAndCancelAllButtons}
            color="primary"
            onClick={handleSelectlAll}
            disabled={!canEvaluateEnrollment() 
              || (checkedEnrollmentRequests.length === enrollmentLists.results.length)}
            size="sm"
          >
            {t("enrollmentList.selectAll")}
        </Button>
        <Button
            color="primary"
            onClick={handleCancelAll}
            disabled={checkedEnrollmentRequests.length === 0}
            outline
            size="sm"
          >
            {t("enrollmentList.cancelAll")}
        </Button>
          <div className={style.searchButtons}>
            <Button
              color="primary"
              onClick={handleToggleCancelEnrollmentConfirmModal}
              disabled={checkedEnrollmentRequests.length === 0}
            >
              {t("enrollmentList.cancelEnrollmentButton")}
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Typography variant="smallBold" component="span">
            {t("enrollmentList.numberOfSelected", {
              numberOfSelected: checkedEnrollmentRequests?.length,
              numberOfPosibleToSelect: enrollmentLists?.results?.length
            })}
          </Typography>       
       </Col>
      </Row>
      {enrollmentLists && enrollmentLists.results.length > 0 && (
        <EnrollmentList
          renderCheckBox={renderCheckBox}
          renderLocationsAndProgramsColumn={renderLocationsAndProgramsColumn}
          tableProps={tableProps}
        />
      )}
      
      <Col
        md={12}
        className={"p-0 text-right mt-2 mb-4"}
      >
        <Button
          size="sm"
          color="primary"
          disabled={!enrollmentLists || enrollmentLists.results.length === 0}
          outline
          icon={<Download />}
          onClick={handleDownloadExcel}
        >
          {t("request.initialConversation.download")}
        </Button>
      </Col>

      <Modal
        type={"confirm"}
        title={t("enrollmentList.cancelEnrollmentConfirmModalTitle")}
        open={openCancelEnrollmentConfirmModal}
        confirmText={t("auth.submit")}
        cancelText={t("common.quit")}
        onClose={handleToggleCancelEnrollmentConfirmModal}
        onConfirm={handleCancelEnrollment}
      >
        <div className={style.modalMessageContent}>
          {t("enrollmentList.cancelEnrollmentConfirmModalMessage")}
        </div>
      </Modal>

      <Modal
        type={"message"}
        open={openEnrollmentCancelledModal}
        title={t("enrollmentList.enrollmentCancelSuccessModalTitle")}
        confirmText={t("common.close")}
        onConfirm={handleBackToEnrollmentList}
        onClose={handleBackToEnrollmentList}
      >
        <div className={style.modalMessageContent}>
          {t("enrollmentList.enrollmentCancelSuccessModalMessage")}
        </div>
      </Modal>
    </>
  );
};
